import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { classNames } from 'primereact/utils';
import { useDispatch, useSelector } from 'react-redux';
import { Dropdown } from 'primereact/dropdown';
import appActions from '../../../appActions';
import DynamicInput from '../../../components/DynamicInput';
import { dateTimeTemplate, isPermissionCodeValid, objtoarr, statusOptions } from '../../../utils/mics';
import EditableJSONViewer from '../../../components/EditableJSONViewer';
import SyncForeCourtConfig from '../../../components/SyncForeCourtConfig';

const PropConfigDetail = ({ pageId }) => {
    
    const { state } = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const writeAccess = isPermissionCodeValid(`${pageId}-W`);

    const toast = useRef(null);

    const propConfig = useSelector((state) => state.propconfig.propconfig);

    const [record, setRecord] = useState({});
    const [isEditMode, setIsEditMode] = useState(false);
    const [isViewMode, setIsViewMode] = useState(false);
    const [propValue, setPropValue] = useState([]);

    const [showEditor, setShowEditor] = useState(true);

    useEffect(() => {

        if (!state) return;
        
        const { data, isView, isEdit } = state;
        setRecord(data || {});
        setIsViewMode(isView || false);
        setIsEditMode(isEdit || false);
        
        const propvalues = data && data.propValue !== null && Object.keys(data.propValue).length !== 0 ? objtoarr(data.propValue) : [];

        setPropValue(propvalues);

    }, [state]);

    const handleSubmit = (data) => {
        if (data.id) {
            dispatch(appActions.PROPCONFIG_UPDATE_REQUEST(data));
        } else {
            dispatch(appActions.PROPCONFIG_CREATE_REQUEST(data));
        }
    };

    const handleSaveJson = (data) => {
        setRecord(prev => ({ ...prev, propValue: data }));
    };

    const handleCopy = (data) => { 
        navigator.clipboard.writeText(data);
    }

    const handleRedirect = useCallback(() => {
        dispatch(appActions.PROPCONFIG_RESET_REQUEST());
        navigate('/ui/prop-confg', { replace: true });
    }, [dispatch, navigate]);

    useEffect(() => {
        if (propConfig?.action === 'create' || propConfig?.action === 'update') {
            handleRedirect();
        }
    }, [propConfig, handleRedirect]);

    const formik = useFormik({
        initialValues: {
            id: record?.id || '',
            propCode: record?.propCode || '',
            propGroup: record?.propGroup || '',
            propValue: record?.propValue || '',
            comment: record?.comment || '',
            createdOn: dateTimeTemplate(record?.createdOn) || '',
            lastUpdated: dateTimeTemplate(record?.lastUpdated) || '',
            status: record && record.status ? statusOptions.find( (status) => status.code === record.status ) : statusOptions.find((status) => status.code === 'Active'),
        },
        enableReinitialize: true,
        validate: (data) => {
            let errors = {};
            if (!data.propCode) {
                errors.propCode = 'Property Code is required.';
            }
            if (!data.propGroup) {
                errors.propGroup = 'Property Group is required.';
            }
            return errors;
        },
        onSubmit: (values) => {
            const data = {
                ...values,
                status: values.status.code ? values.status.code : '',
                propValue: showEditor ? record?.propValue : propValue.reduce(function (acc, item) {
                    if (item.field.trim() !== '' &&  typeof item.value === 'object' ){ 
                        acc[item.field] = item.value;
                    }else if ( item.field.trim() !== '' &&  ( (typeof item.value === 'string' && item.value.trim() !== '') ||  (typeof item.value === 'number' && !isNaN(item.value))) ) {
                        acc[item.field] = typeof item.value === 'string' ? item.value.trim() : item.value;
                    }
                    return acc;
                }, {}),
            };
            if (isEditMode) {
                data['id'] = record.id;
            }
            handleSubmit(data);
        }
    });

    const isFormFieldValid = (name) =>
        !!(formik.touched[name] && formik.errors[name]);

    const getFormErrorMessage = (name) => {
        return (
            isFormFieldValid(name) && (
                <small className="p-error">{formik.errors[name]}</small>
            )
        );
    };

    const handleDynamicInputChange = (data) => { 
        setPropValue(data)
    };

    const renderFormArea = () => {
        return (
            <div className="p-fluid formgrid grid br-2 bg-white p-2 py-2 border-round-xl shadow-1">

                {(isEditMode || isViewMode) && (
                    <div className="col-4 mb-3">
                        <label htmlFor="id">Id</label>
                        <InputText id="id" name="id" value={formik.values.id} onChange={formik.handleChange} disabled />
                    </div>
                )}

                <div className="col-4 mb-3">
                    <label htmlFor="propCode">Property Code*</label>
                    <InputText id="propCode" name="propCode" value={formik.values.propCode} onChange={formik.handleChange} className={classNames({ 'p-invalid': isFormFieldValid('propCode') })} readOnly={isViewMode} />
                    {getFormErrorMessage('propCode')}
                </div>

                <div className="col-4 mb-2">
                    <label htmlFor="propGroup">Property Group*</label>
                    <InputText id="propGroup" name="propGroup" value={formik.values.propGroup} onChange={formik.handleChange} className={classNames({ 'p-invalid': isFormFieldValid('propGroup') })} readOnly={isViewMode} />
                    {getFormErrorMessage('propGroup')}
                </div>

                <div className="col-4 mb-3">
                    <label htmlFor="comment">Comment</label>
                    <InputText id="comment" name="comment" value={formik.values.comment} onChange={formik.handleChange} readOnly={isViewMode} />
                </div>
                {(isEditMode || isViewMode) && (
                    <>
                        <div className="col-4 mb-3">
                            <label htmlFor="createdOn">Created On </label>
                            <InputText id="createdOn" name="createdOn" value={formik.values.createdOn} onChange={formik.handleChange} disabled />
                        </div>
                        <div className="col-4 mb-3">
                            <label htmlFor="lastUpdated">Last Updated</label>
                            <InputText id="lastUpdated" name="lastUpdated" value={formik.values.lastUpdated} onChange={formik.handleChange} disabled />
                        </div>
                         <div className="col-4 mb-3">
                            <label htmlFor="lastUpdatedBy"> Last Updated By </label>
                            <InputText id="lastUpdatedBy" name="lastUpdatedBy" value={formik.values.lastUpdatedBy} onChange={formik.handleChange}  disabled />
                        </div>
                    </>
                )}

                <div className="col-12">

                    <div className="d-flex flex-row">
                        <h4 className='mr-auto'>Property Values</h4>
                        <Button outlined  style={{ width: '130px', height: '35px' }} type='button' label={ showEditor ? 'Edit Row' : 'Edit JSON'} icon="pi pi-pencil" onClick={() => setShowEditor(!showEditor)} className="p-button-success" />
                    </div>
                                       
                </div>

                {!showEditor && (
                    <div className="col-12 mb-2">
                        <DynamicInput fieldArr={propValue} isViewMode={isViewMode} handleChange={handleDynamicInputChange} />
                    </div>
                )}

                <div className="col-12 mb-2">
                    {showEditor && (
                        <EditableJSONViewer
                            jsonData={record}
                            onExit={() => setShowEditor(false)}
                            onSave={handleSaveJson}
                            onCopy={handleCopy}
                            mode={ isViewMode ? 'view' : 'edit'}
                        />
                    )}
                </div>
               
            </div>
        );
    };

    const renderMainButton = () => {
        return (
            <>
                {!isViewMode && 
                    <Button
                        label={isEditMode ? 'Update' : 'Save'}
                        className="p-button-success tp"
                        icon="pi pi-save" type="submit"
                        severity="success" outlined
                        data-pr-tooltip={isEditMode ? 'Update' : 'Save'}
                    />
                }
                <Button
                   label={isViewMode ? 'Back' : 'Cancel'}
                   icon={isViewMode ? 'pi pi-arrow-left' : 'pi pi-times'}
                   className="p-button-secondary pt tp" type="button"
                   data-pr-tooltip={isViewMode ? 'Back' : 'Cancel'}
                   severity="secondary" outlined
                   onClick={() => navigate('/ui/prop-confg')}
                />

                {( writeAccess && formik.values.propCode?.startsWith("PJO_ForecourtConfig")) && 
                
                    <SyncForeCourtConfig toast={toast} tooltipText="Sync Forecourt Configuration" storeId={ formik.values.propCode?.slice(-4) }
                        onSuccess={() => navigate('/ui/prop-confg') }
                    />
                
                }
                
            </>
        );
    };

    const renderSideBar = () => {
        return (
            <div className="p-fluid formgrid grid br-2 bg-white py-4 border-round-xl shadow-1">
                <div className="col-12 mb-2">
                    <label htmlFor="status">Status</label>
                    <Dropdown filter showClear id="status" name="status" value={formik.values.status} onChange={formik.handleChange} options={statusOptions} optionLabel="name" placeholder="Select" disabled={isViewMode} />
                </div>
            </div>
        );
    };

    return (
        <>
            <form onSubmit={formik.handleSubmit}>
                <div className="form_height px-2 mt-2">
                     <div className="d-flex flex-row gap-10 align-items-center mb-1">
                        <h2 className="page-header mr-auto">
                            {isEditMode ? 'Edit Configuration' : 'Configuration Details'}
                        </h2>
                        {renderMainButton()}
                    </div>

                    <div className="grid px-2">
                        <div className="col-9">{renderFormArea()}</div>
                        <div className="col-3 px-3">{renderSideBar()}</div>
                    </div>
                    
                </div>
            </form>
        </>
    );
};

export default PropConfigDetail;
